<template>
	<GlassCard class="card p-3">
		<image-loader
			v-if="itemData.imageSrc"
			:src="itemImage"
			alt="Processed Image"
			class="image" />
		<h2 v-else v-text="itemData.prompt"></h2>

		<div class="status">
			<div v-if="!isOnline" class="network-offline">Network is offline.</div>

			<template v-else>
				<!-- sending / processing -->
				<div v-if="isWorking">
					<div class="loading-spinner">
						<div class="status-update">{{ workingMessage }}</div>
					</div>
					<div class="status-hints">
						<small
							v-if="
								nextSeconds !== null &&
								clientPhase !== 'polling' &&
								clientPhase !== 'sending'
							">
							Next check in {{ nextSeconds }}s
						</small>
						<small v-if="lastUpdateAgo">Last update {{ lastUpdateAgo }}</small>
					</div>
				</div>

				<!-- error -->
				<div v-else-if="itemData.status === 'error'" class="error">
					<p>{{ errorMessage }}</p>
					<p
						v-if="clientPhase === 'retrying' && nextSeconds !== null"
						class="retry-hint">
						Auto-retrying in {{ nextSeconds }}s...
					</p>
					<button
						@click="retryProcess"
						class="btn btn-lg btn-primary mx-2 text-white">
						Retry
					</button>
					<button @click="removeProcess" class="btn btn-lg btn-danger mx-2">
						Remove
					</button>
				</div>

				<!-- completed -->
				<div v-else-if="itemData.status === 'completed'">
					<p>Item completed!</p>
				</div>
			</template>
		</div>
	</GlassCard>
</template>

<script setup>
	import GlassCard from "./GlassCard.vue"
	import {
		ref,
		reactive,
		onMounted,
		onBeforeUnmount,
		onActivated,
		onDeactivated,
		computed,
		watch,
	} from "vue"
	import { useItemQueueStore } from "@/stores/item-queue"
	import Swal from "sweetalert2"
	import ImageLoader from "./ImageLoader.vue"
	import moment from "moment-timezone"
	import { isOnline } from "@/utils/networkBus"
	import { fetchWithAuth } from "@/utils/api-client"
	import getURL from "@/utils/url"
	import EventBus from "@/utils/eventBus"
	import { useUserStore } from "@/stores/user"
	import { App } from "@capacitor/app"

	const userStore = useUserStore()

	const props = defineProps({
		item: { type: Object, required: true },
	})

	// Local mutable copy
	const itemData = reactive({ ...props.item })
	const iqStore = useItemQueueStore()

	const itemImage = computed(() => {
		const src = itemData.imageSrc
		if (!src) return null
		return src.startsWith("data:") || src.startsWith("blob")
			? src
			: getURL("image", src)
	})

	const statusUpdate = ref("Processing...")
	const errorMessage = ref(null)

	// client state
	// idle|sending|processing|streaming|waiting|backoff|retrying|offline|paused|reconnecting|done
	const clientPhase = ref("idle")
	const nextActionAt = ref(null) // timestamp (ms) for next scheduled action (used for send retries)
	const lastContactAt = ref(null) // last time we heard *anything* (status/ping)
	const lastProgressAt = ref(null) // last time the server reported new progress
	const nowTick = ref(Date.now())

	const isWorking = computed(
		() => itemData.status === "sending" || itemData.status === "processing"
	)

	const nextSeconds = computed(() => {
		if (!nextActionAt.value) return null
		return Math.max(0, Math.ceil((nextActionAt.value - nowTick.value) / 1000))
	})

	const lastUpdateAgo = computed(() => {
		const t = lastProgressAt.value || lastContactAt.value
		if (!t) return null
		const seconds = Math.floor((nowTick.value - t) / 1000)
		if (seconds < 45) return null
		return moment(t).fromNow()
	})

	const workingMessage = computed(() => {
		if (!isOnline.value) return "Offline. Waiting for network..."
		switch (clientPhase.value) {
			case "sending":
				return "Sending to server..."
			case "streaming":
				// show the server-driven message while streaming
				return statusUpdate.value || "Processing..."
			case "reconnecting":
				return "Reconnecting..."
			case "retrying":
				return "Retrying send..."
			case "waiting":
			case "backoff":
				return "Awaiting next status check..."
			case "paused":
				return "Paused in background"
			default:
				return statusUpdate.value || "Processing..."
		}
	})

	// Lifecycle/keep-alive state
	const isActive = ref(true)

	// Send / Retry timers
	let isSending = false
	let retryTimer = null
	let sendAbort = null
	let tickTimer = null

	// SSE connection
	let sse = null

	// Constants
	const RETRY_DELAY = 10000
	const SEND_BACKOFF_FACTOR = 2
	const SEND_MAX_DELAY = 60000
	const REQUEST_TIMEOUT = 15000

	let sendDelay = RETRY_DELAY

	function clearTimer(t) {
		if (t) clearTimeout(t)
		return null
	}

	function clearCountdown() {
		nextActionAt.value = null
		if (tickTimer) {
			clearInterval(tickTimer)
			tickTimer = null
		}
	}

	// Keep the countdown accurate and tick once per second
	function startCountdown() {
		nowTick.value = Date.now()
		if (!tickTimer) {
			tickTimer = setInterval(() => (nowTick.value = Date.now()), 1000)
		}
	}

	function closeSSE() {
		if (sse) {
			try {
				sse.close()
			} catch (_) {}
			sse = null
		}
	}

	function stopAllTimers() {
		retryTimer = clearTimer(retryTimer)
		// Abort in-flight send
		if (sendAbort) {
			try {
				sendAbort.abort()
			} catch (_) {}
			sendAbort = null
		}
		// Stop countdown ticks
		clearCountdown()
		// Close streaming connection
		closeSSE()
	}

	function startStream() {
		// prerequisites
		if (!isActive.value || !isOnline.value) {
			if (!isOnline.value) clientPhase.value = "offline"
			return
		}
		if (!itemData.id || itemData.status !== "processing") return
		if (sse) return // already streaming

		const url = `${getURL("add-item", "")}status/stream?id=${encodeURIComponent(
			itemData.id
		)}`
		try {
			// Native EventSource auto-reconnects unless we explicitly close it.
			sse = new EventSource(url, { withCredentials: false })
		} catch (err) {
			// If EventSource constructor fails (rare), fall back to reconnection attempt
			clientPhase.value = "reconnecting"
			return
		}

		clientPhase.value = "streaming"
		statusUpdate.value = "Processing..."

		sse.onopen = () => {
			lastContactAt.value = Date.now()
		}

		// Server emits: event: status / data: {status, output}
		sse.addEventListener("status", async (evt) => {
			lastContactAt.value = Date.now()

			let payload = {}
			try {
				payload = JSON.parse(evt.data || "{}")
			} catch {
				// ignore malformed payload but keep stream alive
				return
			}

			const rawStatus = String(payload.status || "").toLowerCase()
			const terminal = new Set([
				"complete",
				"completed",
				"errored",
				"error",
				"terminated",
			])

			// helper: normalize output (object or JSON string)
			const normalizeOutput = (out) => {
				if (!out) return out
				if (typeof out === "string") {
					try {
						return JSON.parse(out)
					} catch {
						return out
					}
				}
				return out
			}

			// Non-terminal updates (e.g., "running")
			if (!terminal.has(rawStatus)) {
				itemData.status = "processing"
				statusUpdate.value = "Processing..." // <— force the label you want
				lastProgressAt.value = Date.now()
				clientPhase.value = "streaming"
				return
			}

			// Terminal states
			const out = normalizeOutput(payload.output)

			if (rawStatus === "complete" || rawStatus === "completed") {
				if (out?.error) {
					itemData.status = "error"
					errorMessage.value = out.error || "Something happened"
				} else {
					itemData.status = "completed"
					lastProgressAt.value = Date.now()
					await iqStore.remove(itemData.id)
					if (!userStore.isViewingUser()) EventBus.emit("meal-added", out)
				}
				clientPhase.value = "done"
				closeSSE() // important: stop auto-reconnect when done
				return
			}

			// errored / terminated
			itemData.status = "error"
			errorMessage.value = out?.error || payload.message || "Something happened"
			clientPhase.value = "done"
			closeSSE()
		})

		// Heartbeat: event: ping
		sse.addEventListener("ping", (evt) => {
			lastContactAt.value = Date.now()
			// No UI change needed; this keeps "Last update" fresh
		})

		// Server-emitted error payload (our endpoint emits 'error' events on failures)
		sse.addEventListener("error", (evt) => {
			lastContactAt.value = Date.now()
			// If we've already completed or are not active, ensure closed
			if (!isActive.value || itemData.status !== "processing") {
				closeSSE()
				return
			}
			// Leave it to EventSource to auto-reconnect; show reconnecting state for UX
			clientPhase.value = "reconnecting"
		})

		// Network (readyState 2) / fatal close handling
		sse.onerror = () => {
			// If stream hard-closes while still processing, UI goes to reconnecting.
			if (itemData.status === "processing") {
				clientPhase.value = "reconnecting"
			}
		}
	}

	async function trySendToServer() {
		if (isSending || itemData.has_sent || !isOnline.value || !isActive.value)
			return
		isSending = true
		errorMessage.value = null

		let sendTimeout = null
		try {
			// This key is persisted with the queue item before the request. A retry
			// after a refresh therefore resumes the same server workflow.
			if (!itemData.requestId) {
				itemData.requestId = crypto.randomUUID()
				await iqStore.update(itemData.id, itemData)
			}

			sendAbort = new AbortController()
			sendTimeout = setTimeout(() => {
				try {
					sendAbort.abort()
				} catch (_) {}
			}, REQUEST_TIMEOUT)

			clientPhase.value = "sending"

			const formData = new FormData()
			formData.append("request_id", itemData.requestId)
			const utcDate = moment(itemData.mealDateTime).utc()
			formData.append("timestamp", utcDate.unix())

			if (itemData.mealType) formData.append("mealType", itemData.mealType)
			if (itemData.cookType) formData.append("cookType", itemData.cookType)
			if (itemData.prompt) formData.append("prompt", itemData.prompt)
			if (itemData.restaurantName)
				formData.append("restaurant_name", itemData.restaurantName)
			if (itemData.category) formData.append("category", itemData.category)
			if (itemData.targetMode) formData.append("target_mode", itemData.targetMode)
			if (itemData.targetRegion) {
				formData.append("target_region", JSON.stringify(itemData.targetRegion))
			}

			async function appendImage(name, src) {
				if (!src) return
				const resp = await fetch(src, {
					signal: sendAbort.signal,
				})
				const blob = await resp.blob()
				const ext = (blob.type?.split("/")?.[1] || "png").split("+")[0]
				formData.append(name, blob, `${name}.${ext}`)
			}

			if (itemData.targetImageSrc) {
				await appendImage("file", itemData.targetImageSrc)
				await appendImage("original_file", itemData.imageSrc)
			} else {
				await appendImage("file", itemData.imageSrc)
			}

			const response = await fetchWithAuth("/api/add-queue", {
				method: "POST",
				body: formData,
				signal: sendAbort.signal,
			})
			const data = await response.json()
			lastContactAt.value = Date.now()

			if (!response.ok) {
				itemData.status = "error"
				errorMessage.value = data.message || data.error || "Failed to send"
				clientPhase.value = "retrying"
				scheduleRetry()
				return
			}

			const oldId = itemData.id
			itemData.id = data.id
			itemData.file_url = data.file_url
			itemData.timestamp = data.timestamp
			itemData.status = "processing"
			itemData.has_sent = true
			statusUpdate.value = "Processing..."
			lastProgressAt.value = Date.now()
			// Reset send backoff after success
			sendDelay = RETRY_DELAY
			clientPhase.value = "processing"

			retryTimer = clearTimer(retryTimer)
			await iqStore.update(oldId, itemData)

			// Begin streaming live status
			startStream()
		} catch (err) {
			itemData.status = "error"
			errorMessage.value = err.message || "Network error"
			clientPhase.value = "retrying"
			scheduleRetry()
		} finally {
			try {
				if (sendTimeout) clearTimeout(sendTimeout)
			} catch (_) {}
			if (sendAbort) {
				try {
					sendAbort.abort()
				} catch (_) {}
				sendAbort = null
			}
			isSending = false
		}
	}

	function scheduleRetry() {
		if (!isOnline.value || !isActive.value) return
		retryTimer = clearTimer(retryTimer)
		nextActionAt.value = Date.now() + sendDelay
		startCountdown()
		retryTimer = setTimeout(() => {
			if (!isActive.value) return
			itemData.status = "sending"
			errorMessage.value = null
			trySendToServer()
			sendDelay = Math.min(sendDelay * SEND_BACKOFF_FACTOR, SEND_MAX_DELAY)
		}, sendDelay)
	}

	function retryProcess() {
		itemData.status = "sending"
		itemData.has_sent = false
		errorMessage.value = null
		sendDelay = RETRY_DELAY
		trySendToServer()
	}

	async function removeProcess() {
		const q = await Swal.fire({
			icon: "question",
			title: "Are you sure you want to remove this item from the queue?",
			showDenyButton: true,
			confirmButtonText: "Yes",
			denyButtonText: "No",
			reverseButtons: true,
		})
		if (!q.isConfirmed) return
		// Close SSE before removing
		closeSSE()
		await iqStore.remove(itemData.id)
	}

	// Network changes — obey active state
	watch(isOnline, (online) => {
		if (!isActive.value) return
		if (!online) {
			stopAllTimers()
			clientPhase.value = "offline"
			return
		}
		// Came back online
		sendDelay = RETRY_DELAY
		if (itemData.status === "sending") {
			itemData.has_sent
				? ((itemData.status = "processing"), startStream())
				: trySendToServer()
		} else if (itemData.status === "processing" && itemData.id) {
			startStream()
		}
	})

	// Mount/keep-alive hooks
	let appStateListener = null
	onMounted(() => {
		if (!isActive.value) return
		// Listen for app foreground/background to safely pause/resume work
		App.addListener("appStateChange", ({ isActive: appActive }) => {
			isActive.value = appActive
			if (!appActive) {
				stopAllTimers()
				clientPhase.value = "paused"
				return
			}
			// App became active: resume work based on status
			if (itemData.status === "processing" && itemData.id && isOnline.value) {
				startStream()
			} else if (
				itemData.status === "sending" &&
				isOnline.value &&
				!itemData.has_sent
			) {
				trySendToServer()
			}
		}).then((l) => (appStateListener = l))

		// Kick off based on current status
		if (itemData.status === "sending") {
			if (isOnline.value && !itemData.has_sent) {
				trySendToServer()
			} else if (isOnline.value && itemData.has_sent) {
				itemData.status = "processing"
				startStream()
			}
		} else if (
			itemData.status === "processing" &&
			itemData.id &&
			isOnline.value
		) {
			startStream()
		}

		// keep "last updated" ticking
		if (!tickTimer) {
			tickTimer = setInterval(() => (nowTick.value = Date.now()), 1000)
		}
	})

	onActivated(() => {
		isActive.value = true
		if (itemData.status === "processing" && itemData.id && isOnline.value) {
			startStream()
		} else if (
			itemData.status === "sending" &&
			isOnline.value &&
			!itemData.has_sent
		) {
			trySendToServer()
		}
		clientPhase.value =
			itemData.status === "sending"
				? "sending"
				: itemData.status === "processing"
				? "streaming"
				: clientPhase.value
	})

	onDeactivated(() => {
		isActive.value = false
		stopAllTimers() // closes SSE
		clientPhase.value = "paused"
	})

	onBeforeUnmount(() => {
		isActive.value = false
		stopAllTimers() // closes SSE and timers
		isSending = false
		if (appStateListener?.remove) appStateListener.remove()
	})
</script>

<style lang="less" scoped>
	.card {
		border: none;
		border-radius: 8px;
		padding: 16px;
		box-shadow: none;
		max-width: 400px;
		margin: auto;
		text-align: center;
		width: 100%;
		> h2 {
			color: #ff6600;
		}
	}
	.image {
		::v-deep(> img) {
			width: 100%;
			height: auto;
			border-radius: 8px;
		}
	}
	.status {
		margin-top: 16px;
	}
	.loading-spinner {
		/* Full-width pill bar with teal base and shimmer */
		width: 100%;
		height: 52px;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 12px;
		background-color: #0ea5a9; /* teal 500 */
		overflow: hidden;
		box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08),
			0 2px 8px rgba(14, 165, 233, 0.35);
	}
	/* animated shimmer sweep */
	.loading-spinner::after {
		content: "";
		position: absolute;
		inset: 0;
		background: linear-gradient(
			90deg,
			rgba(255, 255, 255, 0) 0%,
			rgba(255, 255, 255, 0.28) 50%,
			rgba(255, 255, 255, 0) 100%
		);
		transform: translateX(-100%);
		animation: shimmer 1.25s ease-in-out infinite;
	}
	.status-update {
		position: absolute;
		z-index: 1;
		font-size: 1rem;
		font-weight: 700;
		color: #e6f7f8; /* high-contrast over teal */
		padding: 0 10px;
		text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
	}
	.status-hints {
		margin-top: 8px;
		display: flex;
		flex-direction: column;
		gap: 4px;
		align-items: center;
		color: #0ea5a9; /* match loading bar accent */
		> small {
			font-size: 0.95rem;
			opacity: 0.95;
		}
	}
	.retry-hint {
		font-size: 0.95rem;
		color: #222;
		margin-top: 6px;
	}
	@keyframes shimmer {
		100% {
			transform: translateX(100%);
		}
	}
	.error,
	.network-offline {
		color: red;
		font-weight: bold;
	}
	.network-offline {
		margin-bottom: 8px;
	}
</style>
